import React from 'react';
import styled from 'styled-components';
import { RebelColors } from '../../../../styles/rebel-experiences';

/**
 * Display ranking tally for a REBEL experience
 * Shows the distribution of rankings or average ranking
 * @param {object} allRankings - All rankings from all voters { voterId: { timestamp, votes: {expId: rank} } }
 * @param {string} experienceId - The experience ID to calculate tally for
 * @param {string} displayMode - 'average' or 'distribution' (default: 'average')
 */
const TallyDisplay = ({ allRankings, experienceId, displayMode = 'average' }) => {

    // Calculate tally for this experience
    const calculateTally = () => {
        if (!allRankings || Object.keys(allRankings).length === 0) {
            return {};
        }

        const tally = {};
        Object.values(allRankings).forEach(voter => {
            if (voter.votes && voter.votes[experienceId]) {
                const rank = voter.votes[experienceId];
                tally[rank] = (tally[rank] || 0) + 1;
            }
        });
        return tally;
    };

    // Calculate average ranking
    const calculateAverage = (tally) => {
        const votes = Object.entries(tally);
        if (votes.length === 0) return null;

        const sum = votes.reduce((acc, [rank, count]) =>
            acc + (parseInt(rank) * count), 0);
        const total = votes.reduce((acc, [rank, count]) =>
            acc + count, 0);

        return {
            average: (sum / total).toFixed(1),
            totalVotes: total
        };
    };

    const tally = calculateTally();
    const tallyEntries = Object.entries(tally).sort((a, b) => parseInt(a[0]) - parseInt(b[0]));

    // If no votes yet
    if (tallyEntries.length === 0) {
        return (
            <TallyContainer>
                <NoVotesText>—</NoVotesText>
            </TallyContainer>
        );
    }

    // Display average mode
    if (displayMode === 'average') {
        const { average, totalVotes } = calculateAverage(tally);
        return (
            <TallyContainer>
                <AverageRanking>{average}</AverageRanking>
            </TallyContainer>
        );
    }

    // Display distribution mode
    return (
        <TallyContainer>
            {tallyEntries.map(([rank, count]) => (
                <TallyRow key={rank} rank={parseInt(rank)}>
                    {getRankDisplay(parseInt(rank))}: {count}
                </TallyRow>
            ))}
        </TallyContainer>
    );
};

// Helper to get rank display (medals for top 3)
const getRankDisplay = (rank) => {
    switch (rank) {
        case 1: return '🥇';
        case 2: return '🥈';
        case 3: return '🥉';
        default: return `#${rank}`;
    }
};

export default TallyDisplay;

const TallyContainer = styled.div`
    display: flex;
    flex-direction: column;
    font-size: 0.85rem;
    text-align: center;
    align-items: center;
    justify-content: center;
    width: 100%;
`;

const TallyRow = styled.div`
    color: ${props => {
        if (props.rank === 1) return RebelColors.gold;
        if (props.rank === 2) return '#C0C0C0';
        if (props.rank === 3) return '#CD7F32';
        return RebelColors.primary;
    }};
    font-weight: ${props => props.rank <= 3 ? '600' : '400'};
    white-space: nowrap;
`;

const AverageRanking = styled.div`
    color: ${RebelColors.gold};
    font-weight: 600;
    font-size: 0.95rem;
    line-height: 1.4;
    padding: 4px 0;
`;

const VoteCount = styled.div`
    color: ${RebelColors.primary};
    font-size: 0.75rem;
    opacity: 0.8;
`;

const NoVotesText = styled.div`
    color: ${RebelColors.primary};
    font-size: 0.85rem;
    opacity: 0.4;
    line-height: 1.4;
    padding: 4px 0;
`;
